import React, { useState, forwardRef, useMemo, useCallback, useRef } from 'react';
import { find, uniqBy } from 'lodash';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import { Form } from 'antd';

const MAPPING_TYPES = {
  CLUSTERS: 'cluster',
  DEPLOYMENT: 'deployment',
};

const MAPPING_TYPE_OPTIONS = [
  { label: 'Clusters Namespace', value: MAPPING_TYPES.CLUSTERS },
  { label: 'Deployment', value: MAPPING_TYPES.DEPLOYMENT },
];

// Stable apiParams objects defined outside component to prevent infinite re-renders
const CLUSTER_API_PARAMS = { type: MAPPING_TYPES.CLUSTERS };
const DEPLOYMENT_API_PARAMS = { type: MAPPING_TYPES.DEPLOYMENT };

const RedhatACSMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
  const [form] = Form.useForm();
  const [initialValues] = useState(() => {
    const mappingType = props.record?.mappingType || MAPPING_TYPES.CLUSTERS;
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        mappingType,
      },
    ];
  });

  const { cluster, clusterName, namespace, deploymentIds, deploymentNames } = props.record || {};
  const clustersRef = useRef(
    cluster
      ? [{ id: cluster, name: clusterName, otherProperties: { ...(namespace && { namespaces: namespace.split(',') }) } }]
      : []
  );
  const deploymentsRef = useRef(
    deploymentIds && deploymentIds !== 'All'
      ? deploymentIds.split(',').map((id, index) => ({
          id,
          name: deploymentNames?.split(',')[index] || id
        }))
      : []
  );

  const fetchMappingData = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { ...params, login_id: props.loginConfigId });
    return {
      promise: promise.then((data) => {
        if (params.type === MAPPING_TYPES.CLUSTERS) {
          clustersRef.current = uniqBy([...data.projects, ...(clustersRef.current || [])], 'name');
        } else if (params.type === MAPPING_TYPES.DEPLOYMENT) {
          deploymentsRef.current = uniqBy([...data.projects, ...(deploymentsRef.current || [])], 'name');
        }
        return data.projects
      }), cancel
    }
  }, [props.toolName, props.loginConfigId]);

  const getExtraFieldsData = (config) => {
    const { cluster, namespace, mappingType, deploymentIds } = config;
    if (mappingType === MAPPING_TYPES.CLUSTERS) {
      const clusterName = find(clustersRef.current, (obj) => obj.id === cluster)?.name;
      return ({
        mappingType,
        cluster,
        clusterName,
        namespace: namespace.join()
      });
    }
    const deploymentNames = deploymentIds?.length
      ? deploymentsRef.current.filter(obj => deploymentIds.includes(obj.id)).map(obj => obj.name).join()
      : 'All';
    return ({
      mappingType,
      deploymentIds: deploymentIds?.join() || 'All',
      deploymentNames
    });
  }

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { namespace, deploymentIds, mappingType } = config;
    return {
      mappingType : mappingType ? mappingType : MAPPING_TYPES.CLUSTERS, 
      namespace: namespace?.split(','),
      deploymentIds: deploymentIds === 'All' || !deploymentIds ? [] : deploymentIds.split(',')
    };
  }, []);

  const createProjectChangeHandler = useCallback((fieldName) => () => {
    const configurations = form.getFieldValue("configurations");
    configurations[fieldName] = {
      ...configurations[fieldName],
      namespace: undefined,
    }
    form.setFieldsValue({ configurations: configurations });
  }, [form])

  const projectListRender = useCallback((listField) => {
    const selectedType = form.getFieldsValue().configurations[listField.name]?.mappingType;
    const handleClusterChange = createProjectChangeHandler(listField.name);
    const clusterFields = [
      {
        name: 'cluster',
        colSpan: '12',
        type: FieldTypes.SELECT,
        label: 'Cluster',
        disabled: disableProjectFields,
        required: true,
        props: {
          api: fetchMappingData,
          apiParams: CLUSTER_API_PARAMS,
          autoFocus: !disableProjectFields,
          allowClear: true,
          onChange: handleClusterChange,
        }
      },
      {
        name: 'namespace',
        colSpan: '12',
        type: FieldTypes.MULTI_SELECT,
        required: true,
        label: 'Name Space',
        disabled: disableProjectFields,
        props: {
          allowClear: true,
          setExtraProps: () => {
            const allConfigs = form.getFieldsValue().configurations;
            const clusterID = allConfigs[listField.name]?.cluster;
            const selectValue = find(clustersRef.current, { id: clusterID });
            const spaceOptions = selectValue?.otherProperties?.namespaces ? selectValue.otherProperties.namespaces : [];
            return {
              options: spaceOptions,
              resetOptionsProp: clusterID,
              disabled: !clusterID || disableProjectFields
            }
          }
        }
      }
    ];

    const deploymentFields = [
      {
        name: 'deploymentIds',
        colSpan: '12',
        type: FieldTypes.MULTI_SELECT,
        label: 'Deployments',
        placeholder: 'Leave blank to select all',
        disabled: disableProjectFields,
        props: {
          api: fetchMappingData,
          apiParams: DEPLOYMENT_API_PARAMS,
          autoFocus: !disableProjectFields,
          allowClear: true,
          onChange: handleClusterChange,
        }
      }
    ];

    const formFields = selectedType === MAPPING_TYPES.CLUSTERS ? clusterFields : deploymentFields;

    return (
      <div className='m-b-sm'>
        {generateFormFields(formFields, 24, listField)}
      </div>
    );
  }, [fetchMappingData, form, disableProjectFields, createProjectChangeHandler]);

  const extraFields = useMemo(() => ([
    {
      name: 'mappingType',
      type: FieldTypes.RADIO,
      options: MAPPING_TYPE_OPTIONS,
    },
    {
      name: (listField) => [listField.name, "projectList"],
      render: projectListRender
    }
  ]), [projectListRender]);
  
  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
    />
  )
})

export default RedhatACSMappingForm;